<template>
	<VerticalLayout>
		<LabeledSwitch
			v-model="showParsedData"
			:leftLabel="$t('baselines-edit.lastGGA.showRawDataLabel')"
			:rightLabel="$t('baselines-edit.lastGGA.showParsedDataLabel')"
			small
		/>
		<div class="scrollable-x" v-if="!showParsedData">
			<TypographyParagraph v-for="item in lastRawData" :key="item.id">
				<pre class="raw-nmea-data">{{ item.data }}</pre>
			</TypographyParagraph>
		</div>
		<BaselineStatsCharts
			v-if="showParsedData"
			:qualityOptions="qualityOptions"
			v-model:selectedQuality="selectedQualityRef"
			:epochCounts="epochCounts"
			:allSeries="allSeries"
			:allOptions="allOptions"
			:isStatsLoading="isStatsLoading"
		/>
		<div class="scrollable-x" v-if="showParsedData">
			<Table
				class="test-last-gga-table"
				:columns="columns"
				:data-source="lastGGA"
				:pagination="false"
				size="small"
			>
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'epoch'">
						<DateTimeDisplay :date="record.epoch" format="YYYY-MM-DD HH:mm:ss" />
					</template>
				</template>
			</Table>
		</div>
	</VerticalLayout>
</template>

<script>
import { ref, toRef, onMounted, watch } from 'vue';
import { useVModel } from '@vueuse/core';

import { TypographyParagraph, Table } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import { useInterval } from '@/utils/useInterval';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import DateTimeDisplay from '@/components/common/DateTimeDisplay.vue';
import BaselineStatsCharts from '@/components/addBaseline/BaselineStatsCharts.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';


const QUALITY = {
	0: 'noFix',
	1: 'fix',
	2: 'differential',
	3: 'notApplicable',
	4: 'rtkFixed',
	5: 'rtkFloat',
	6: 'deadReckoning',
};


export default {
	components: {
		TypographyParagraph, Table,
		LabeledSwitch, BaselineStatsCharts, DateTimeDisplay,
		VerticalLayout,
	},
	props: {
		baselineId: {
			type: Number,
			required: true,
		},
		isBaselineActive: {
			type: Boolean,
			required: true,
		},
		selectedQuality: {
			type: Number,
			required: true,
		},
		qualityOptions: {
			type: Array,
			required: true,
		},
		epochCounts: {
			type: Object,
			required: true,
		},
		allSeries: {
			type: Array,
			required: true,
		},
		allOptions: {
			type: Object,
			required: true,
		},
		isStatsLoading: {
			type: Boolean,
			required: true,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const isBaselineActiveRef = toRef(props, 'isBaselineActive');
		const showParsedData = ref(true);
		const lastRawData = ref([]);

		const updateLastRawData = () => {
			Api.baselineGetLastRawData(props.baselineId, data => {
				lastRawData.value = data.last_raw_data;
			});
		};

		const columns = Consts.lastGGATableColumnsComp;
		const lastGGA = ref([]);
		const formatDeg = Utils.formatFractions(8);
		const formatM = Utils.formatFractions(3);

		const updateLastGGA = () => {
			Api.baselineGetLastGGA(props.baselineId, data => {
				if (data.status === 'ok') {
					lastGGA.value = data.last_gga.map(item => ({
						...item,
						lat: formatDeg(item.lat),
						lon: formatDeg(item.lon),
						height: formatM(item.height),
						n_coord: (item.n_coord != null) ? formatM(item.n_coord) : '—',
						e_coord: (item.e_coord != null) ? formatM(item.e_coord) : '—',
						u_coord: (item.u_coord != null) ? formatM(item.u_coord) : '—',
						quality: t('baselines-edit.qualityStatus.' + QUALITY[item.quality]),
					}));
				} else {
					lastGGA.value = [];
				}
			});
		};

		useInterval(() => {
			if (isBaselineActiveRef.value && !showParsedData.value) {
				updateLastRawData();
			}
			if (isBaselineActiveRef.value && showParsedData.value) {
				updateLastGGA();
			}
		}, 2000, false);

		onMounted(() => {
			updateLastRawData();
			updateLastGGA();
		});

		watch(showParsedData, () => {
			updateLastRawData();
			updateLastGGA();
		});

		const selectedQualityRef = useVModel(props, 'selectedQuality', emit);

		return {
			showParsedData,
			lastRawData,
			columns,
			lastGGA,
			selectedQualityRef,
		};
	},
};
</script>

<style scoped>
.raw-nmea-data {
	margin-top: 0;
}
</style> 